/**
 * SollGapSection — the Soll-Deckung & Gap table (P2 Placement Slice 2, Ch 5). Each row is a
 * catalog rule (Pflicht-Status + Zieltext) merged with the stored assessment: the broker enters a
 * Soll (target) and Ist (actual) value, the server computes the gap + status, and the per-row
 * Ampel (deriveDeckungAmpel, rendered via the lifted StatusDot map) shows Gedeckt/Luecke/… The
 * broker may also mark a rule «nicht anwendbar». Per-row PUT on blur, but only when a value
 * actually changed vs the loaded row (no commit on a bare tab-through).
 *
 * Behind the `policen.risiko_deckung.soll` Kachel visibility key.
 */

import { useState } from 'react';
import { SOLL_PFLICHT_STATUS_LABELS, DECKUNG_STATUS_LABELS, DECKUNG_STATUS } from '@brokly/shared';
import type { DeckungItem, DeckungPutBody } from '@brokly/shared';
import { StatusDot } from '../../../../components/ui/StatusDot';
import { AMPEL_TONE, AMPEL_LABEL } from '../risikoprofil/ampel';

function numOrNull(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

export function SollGapSection({
  items,
  canWrite,
  onPut,
}: {
  items: DeckungItem[];
  canWrite: boolean;
  onPut: (ruleKey: string, body: DeckungPutBody) => void;
}) {
  return (
    <section aria-labelledby="pr-soll" className="rounded-lg border bg-card p-4" data-testid="pr-soll">
      <h3 id="pr-soll" className="text-sm font-semibold text-foreground">
        Soll-Deckung & Gap
      </h3>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pr-3 font-medium">Deckung</th>
              <th className="py-2 pr-3 font-medium">Pflicht</th>
              <th className="py-2 pr-3 font-medium">Soll (CHF)</th>
              <th className="py-2 pr-3 font-medium">Ist (CHF)</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">Beurteilung</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <SollRow key={item.rule_key} item={item} canWrite={canWrite} onPut={onPut} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SollRow({
  item,
  canWrite,
  onPut,
}: {
  item: DeckungItem;
  canWrite: boolean;
  onPut: (ruleKey: string, body: DeckungPutBody) => void;
}) {
  const [target, setTarget] = useState(item.target_value ?? '');
  const [actual, setActual] = useState(item.actual_value ?? '');

  // The loaded (server) values, normalised to number|null for the dirty check.
  const loadedTarget = item.target_value == null ? null : Number(item.target_value);
  const loadedActual = item.actual_value == null ? null : Number(item.actual_value);

  function commit(extra: Partial<DeckungPutBody> = {}) {
    onPut(item.rule_key, { target_value: numOrNull(target), actual_value: numOrNull(actual), ...extra });
  }

  // Commit-on-blur only when a value actually changed vs the loaded row — a bare tab-through must
  // not write an audit + activity row (m5; mirrors Slice 1's BasisspartenSection on-change commit).
  function commitIfDirty() {
    if (numOrNull(target) === loadedTarget && numOrNull(actual) === loadedActual) return;
    commit();
  }

  return (
    <tr className="border-b last:border-0 align-top" data-testid={`pr-soll-row-${item.rule_key}`}>
      <td className="py-2 pr-3">
        <div className="font-medium text-foreground">{item.label}</div>
        <div className="text-xs text-muted-foreground">
          {item.ziel_text}
          {item.rechtsgrundlage ? ` (${item.rechtsgrundlage})` : ''}
        </div>
      </td>
      <td className="py-2 pr-3">
        <span className="text-xs text-foreground">{SOLL_PFLICHT_STATUS_LABELS[item.pflicht_status]}</span>
      </td>
      <td className="py-2 pr-3">
        <input
          type="number"
          disabled={!canWrite}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          onBlur={commitIfDirty}
          placeholder={item.ziel_default_chf != null ? String(item.ziel_default_chf) : ''}
          aria-label={`Soll ${item.label}`}
          className="w-28 rounded-md border bg-background px-2 py-1 text-xs mono-num"
          data-testid={`pr-soll-target-${item.rule_key}`}
        />
      </td>
      <td className="py-2 pr-3">
        <input
          type="number"
          disabled={!canWrite}
          value={actual}
          onChange={(e) => setActual(e.target.value)}
          onBlur={commitIfDirty}
          aria-label={`Ist ${item.label}`}
          className="w-28 rounded-md border bg-background px-2 py-1 text-xs mono-num"
          data-testid={`pr-soll-actual-${item.rule_key}`}
        />
      </td>
      <td className="py-2 pr-3">
        <span className="text-xs text-foreground">{DECKUNG_STATUS_LABELS[item.status]}</span>
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-col gap-1">
          <StatusDot tone={AMPEL_TONE[item.ampel]} label={AMPEL_LABEL[item.ampel]} />
          {canWrite && (
            <button
              type="button"
              onClick={() =>
                commit({ status: item.status === DECKUNG_STATUS.NOT_APPLICABLE ? 'unknown' : 'not_applicable' })
              }
              className="text-left text-xs text-muted-foreground underline hover:text-foreground"
              data-testid={`pr-soll-na-${item.rule_key}`}
            >
              {item.status === DECKUNG_STATUS.NOT_APPLICABLE ? 'Wieder bewerten' : 'Nicht anwendbar'}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
